type ShippingAddress = {
  address1?: string | null;
  zip?: string | null;
  city?: string | null;
  country?: string | null;
  country_code?: string | null;
};

const DPD_URLS: Record<string, string> = {
  FR: "https://www.dpd.fr/recherche-relais",
  GB: "https://pickup.dpd.co.uk/",
  DE: "https://my.dpd.de/shopfinder.aspx",
  ES: "https://www.seur.com/miseur/buscador-tiendas",
  IT: "https://www.mybrt.it/it/mybrt/parcel-shops/search",
  AT: "https://www.dpd.com/at/en/pickup-parcel-shops/",
  CH: "https://www.dpd.com/ch/en/dpd-pickup-parcelshop/",
  BE: "https://www.dpd.com/be/en/pickup/find-parcelshop/",
  NL: "https://www.dpd.com/nl/nl/pickup/parcelshop-vinden/",
  PL: "https://www.dpd.com/pl/pl/pickup/znajdz-paczkomat/",
  PT: "https://www.dpd.com/pt/en/pickup-points/parcelshop-finder/",
};

const UPS_URLS: Record<string, string> = {
  FR: "https://www.ups.com/dropoff?loc=fr_FR",
  GB: "https://www.ups.com/dropoff?loc=en_GB",
  DE: "https://www.ups.com/dropoff?loc=de_DE",
  ES: "https://www.ups.com/dropoff?loc=es_ES",
  IT: "https://www.ups.com/dropoff?loc=it_IT",
  AT: "https://www.ups.com/dropoff?loc=de_AT",
  CH: "https://www.ups.com/dropoff?loc=de_CH",
  BE: "https://www.ups.com/dropoff?loc=fr_BE",
  NL: "https://www.ups.com/dropoff?loc=nl_NL",
  PL: "https://www.ups.com/dropoff?loc=pl_PL",
  PT: "https://www.ups.com/dropoff?loc=pt_PT",
  US: "https://www.ups.com/dropoff?loc=en_US",
  CA: "https://www.ups.com/dropoff?loc=en_CA",
  MX: "https://www.ups.com/dropoff?loc=es_MX",
};

type CarrierEntry = { name: string; country?: string; redirect: string };

function buildCarrierMap(addr: ShippingAddress): CarrierEntry[] {
  const cc = addr.country_code ?? "";
  const zip = addr.zip ?? "";
  const city = addr.city ?? "";
  const address1 = addr.address1 ?? "";
  const country = addr.country ?? "";

  return [
    { name: "chrono-shop2shop", country: "ES", redirect: "https://www.seur.com/miseur/buscador-tiendas" },
    { name: "chrono-shop2shop", country: "FR", redirect: `https://www.chronoshop2shop.fr/shop2shop-en-ligne/#/ou-nous-trouver/${zip}` },
    { name: "chrono-shop2shop", redirect: "https://www.chronopost.fr/expeditionAvanceeSec/ounoustrouver.html" },
    { name: "crosslog", country: "FR", redirect: `https://www.chronoshop2shop.fr/shop2shop-en-ligne/#/ou-nous-trouver/${zip}` },
    { name: "crosslog", redirect: "https://www.chronopost.fr/expeditionAvanceeSec/ounoustrouver.html" },
    { name: "chronopost", country: "ES", redirect: "https://www.seur.com/miseur/buscador-tiendas" },
    { name: "chronopost", redirect: "https://www.chronopost.fr/expeditionAvanceeSec/ounoustrouver.html" },
    { name: "mondial-relay", redirect: `https://www.mondialrelay.fr/trouver-le-point-relais-le-plus-proche-de-chez-moi?country=${cc}&postcode=${zip}` },
    { name: "fake-dpd", redirect: DPD_URLS[cc] ?? DPD_URLS["FR"] },
    { name: "dpd", redirect: DPD_URLS[cc] ?? DPD_URLS["FR"] },
    { name: "dpdUclUk", redirect: DPD_URLS[cc] ?? DPD_URLS["FR"] },
    { name: "dhl-express", redirect: `https://locator.dhl.com/results?countryCode=${cc}&address=${address1},${city}` },
    { name: "dhl-economy-select", redirect: `https://locator.dhl.com/results?countryCode=${cc}&address=${address1},${city}` },
    { name: "dhl-tax-free", redirect: `https://locator.dhl.com/results?countryCode=${cc}&address=${address1},${city}` },
    { name: "global-e", redirect: `https://locator.dhl.com/results?countryCode=${cc}&address=${address1},${city}` },
    { name: "logfret", redirect: UPS_URLS[cc] ?? UPS_URLS["FR"] },
    { name: "ups-standard", redirect: UPS_URLS[cc] ?? UPS_URLS["FR"] },
    { name: "ups-saver", redirect: UPS_URLS[cc] ?? UPS_URLS["FR"] },
    { name: "ups-ground", redirect: UPS_URLS[cc] ?? UPS_URLS["FR"] },
    { name: "colissimo", country: "IT", redirect: `https://www.poste.it/cerca-mappe-app/?vieni-in-poste&search=${address1} ${zip} ${city}` },
    { name: "colissimo", country: "ES", redirect: "https://www.correos.es/es/en/individuals/receive/our-drop-off-and-collection-points/citypaq" },
    { name: "colissimo", country: "NL", redirect: "https://www.postnl.nl/en/locationfinder/" },
    { name: "colissimo", redirect: `https://localiser.laposte.fr/?jesuis=particulier&qp=${address1}, ${zip}, ${city}, ${country}` },
    { name: "fedex", redirect: "https://local.fedex.com/" },
  ];
}

/** Returns the relay/dropoff URL for a carrier + shipping address, or null if not supported. "bleckmann" never shows the button. */
export function getCarrierRelayUrl(carrier: string, shippingAddress: ShippingAddress): string | null {
  if (carrier === "bleckmann") return null;
  const cc = shippingAddress.country_code ?? "";
  const entries = buildCarrierMap(shippingAddress);
  const match = entries.find((e) => e.name === carrier && (!e.country || e.country === cc))
    ?? entries.find((e) => e.name === carrier && !e.country);
  return match?.redirect ?? null;
}
